import {
	ActionBar,
	SpinnerWithBackground,
	Text,
	TextArea,
	Toggle,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import { useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useEnpsStore } from '../store'
import type { EnpsConfigurationTabInput } from './types'

/**
 * @fileoverview eNPS Configuration Tab
 *
 * Customize survey questions
 */

export const EnpsConfigurationTab = () => {
	const { organizationId: _organizationId } = useParams<{
		organizationId: string
		enpsId: string
	}>()
	const navigate = useNavigate()
	const { enpsConfig, useEditEnpsConfig } = useEnpsStore()
	const { editEnpsConfig, isLoading } = useEditEnpsConfig(enpsConfig?.id)

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty },
		reset,
	} = useForm<EnpsConfigurationTabInput>({
		defaultValues: {
			questionText: enpsConfig?.questionText || '',
			followUpQuestionText: enpsConfig?.followUpQuestionText || '',
			textFeedbackEnabled: enpsConfig?.textFeedbackEnabled ?? true,
		},
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	const textFeedbackEnabled = useWatch({ control, name: 'textFeedbackEnabled' })

	useEffect(() => {
		if (enpsConfig) {
			reset({
				questionText: enpsConfig.questionText,
				followUpQuestionText: enpsConfig.followUpQuestionText,
				textFeedbackEnabled: enpsConfig.textFeedbackEnabled,
			})
		}
	}, [enpsConfig, reset])

	const handleSubmitInternal = async (data: EnpsConfigurationTabInput) => {
		await editEnpsConfig(data)
		reset(data)
	}

	return (
		<form onSubmit={handleSubmit(handleSubmitInternal)} className='relative pb-12 mt-6 w-full'>
			{isLoading && <SpinnerWithBackground />}
			<div className='grid grid-cols-1 gap-6 px-6 max-w-4xl'>
				<div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
					<Text variant='small' weight='semibold' className='text-blue-900'>
						NPS Survey Format
					</Text>
					<Text variant='xsmall' className='text-blue-700 mt-2'>
						Participants will be asked to rate their likelihood on a scale of 0-10, followed by an optional feedback
						question. The NPS score is calculated as: % Promoters (9-10) - % Detractors (0-6).
					</Text>
				</div>

				<Controller
					name='questionText'
					control={control}
					rules={{
						required: 'Main question is required',
						minLength: { value: 10, message: 'Question should be at least 10 characters' },
					}}
					render={({ field }) => (
						<TextArea
							label='Main Question'
							placeholder='How likely are you to recommend our organization as a place to work?'
							variant={errors.questionText ? 'error' : 'default'}
							hint={errors.questionText?.message as string}
							value={field.value}
							onChange={field.onChange}
							onBlur={field.onBlur}
							rows={3}
							required
						/>
					)}
				/>

				<div className='border border-gray-200 rounded-lg p-4'>
					<div className='flex items-center justify-between mb-4'>
						<div>
							<Text variant='small' weight='semibold' className='text-gray-900'>
								Enable Follow-up Question
							</Text>
							<Text variant='xsmall' className='text-gray-500 mt-1'>
								Allow participants to provide additional feedback after rating
							</Text>
						</div>
						<Controller
							name='textFeedbackEnabled'
							control={control}
							render={({ field }) => <Toggle checked={field.value} onChange={field.onChange} size='small' />}
						/>
					</div>

					{textFeedbackEnabled && (
						<Controller
							name='followUpQuestionText'
							control={control}
							rules={{
								required: textFeedbackEnabled ? 'Follow-up question is required' : false,
								minLength: { value: 5, message: 'Question should be at least 5 characters' },
							}}
							render={({ field }) => (
								<TextArea
									label='Follow-up Question'
									placeholder='What is the primary reason for your score?'
									variant={errors.followUpQuestionText ? 'error' : 'default'}
									hint={errors.followUpQuestionText?.message as string}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									rows={2}
									required
								/>
							)}
						/>
					)}
				</div>
			</div>
			<ActionBar
				rightActions={[
					{
						variant: 'secondary' as const,
						label: 'Cancel',
						onClick: () => navigate('/enps'),
					},
					{
						variant: 'primary' as const,
						label: 'Save',
						onClick: handleSubmit(handleSubmitInternal),
					},
				]}
			/>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</form>
	)
}
